import { firestore as db } from '../firebase/config';
import { collection, doc, getDoc, setDoc, getDocs, deleteDoc, query, where, updateDoc } from 'firebase/firestore';

const COLLECTION_NAME = 'authorized_users';

// Interface for authorized user data
export interface AuthorizedUser {
  email: string;
  role: 'owner' | 'admin' | 'editor';
  displayName?: string;
  addedBy?: string;
  addedAt: Date;
  lastLogin?: Date;
}

// Get all authorized users
export const getAllAuthorizedUsers = async (): Promise<AuthorizedUser[]> => {
  try {
    const usersRef = collection(db, COLLECTION_NAME);
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        email: doc.id,
        addedAt: data.addedAt?.toDate ? new Date(data.addedAt.toDate()) : new Date(data.addedAt),
        lastLogin: data.lastLogin?.toDate ? new Date(data.lastLogin.toDate()) : data.lastLogin ? new Date(data.lastLogin) : undefined,
      } as AuthorizedUser;
    });
  } catch (error) {
    console.error('Error fetching authorized users:', error);
    throw error;
  }
};

// Check if a user is authorized
export const isUserAuthorized = async (email: string): Promise<boolean> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, email.toLowerCase());
    const userDoc = await getDoc(userRef);
    return userDoc.exists();
  } catch (error) {
    console.error(`Error checking if user ${email} is authorized:`, error);
    return false;
  }
};

// Get user role
export const getUserRole = async (email: string): Promise<string | null> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, email.toLowerCase());
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data().role;
    }
    return null;
  } catch (error) {
    console.error(`Error getting role for user ${email}:`, error);
    return null;
  }
};

// Add an authorized user
export const addAuthorizedUser = async (userData: Omit<AuthorizedUser, 'addedAt'>): Promise<void> => {
  try {
    const email = userData.email.toLowerCase();
    const userRef = doc(db, COLLECTION_NAME, email);
    
    await setDoc(userRef, {
      ...userData,
      email, // Store normalized email
      addedAt: new Date(),
    });
  } catch (error) {
    console.error('Error adding authorized user:', error);
    throw error;
  }
};

// Remove an authorized user
export const removeAuthorizedUser = async (email: string): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, email.toLowerCase());
    await deleteDoc(userRef);
  } catch (error) {
    console.error(`Error removing authorized user ${email}:`, error);
    throw error;
  }
};

// Update user last login
export const updateUserLastLogin = async (email: string): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, email.toLowerCase());
    await updateDoc(userRef, {
      lastLogin: new Date()
    });
  } catch (error) {
    console.error(`Error updating last login for user ${email}:`, error);
    // Don't throw error to prevent blocking login flow
  }
};

// Update user role
export const updateUserRole = async (email: string, role: AuthorizedUser['role']): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTION_NAME, email.toLowerCase());
    await updateDoc(userRef, { role });
  } catch (error) {
    console.error(`Error updating role for user ${email}:`, error);
    throw error;
  }
};

// Initialize the owner account if no users exist
export const initializeOwnerAccount = async (ownerEmail: string): Promise<void> => {
  try {
    const users = await getAllAuthorizedUsers();
    
    if (users.length === 0) {
      // No users exist, add the owner account
      await addAuthorizedUser({
        email: ownerEmail,
        role: 'owner',
        displayName: 'Site Owner',
      });
      console.log(`Initialized owner account for ${ownerEmail}`);
    }
  } catch (error) {
    console.error('Error initializing owner account:', error);
  }
}; 